<script setup lang="ts">
import PageHeader from '@/components/Globals/PageHeader.vue'
import Loader from '@/components/Globals/Feedbacks/Loader.vue'
import ListingFilters from '@/components/Globals/ListingFilters.vue'
import IcDeleteClose from '@/components/Icons/IcDeleteClose.vue'
import IcEdit from '@/components/Icons/IcEdit.vue'
import { useI18n } from '@/composable/useI18n'
import { getSensorColor } from '@/interfaces/enums'
import DefaultLayout from '@/layouts/DefaultLayout.vue'
import { useGroupStore } from '@/store/group'
import { useSensorStore } from '@/store/sensor'
import EditGroupModal from '@/views/Group/modals/EditGroupModal.vue'
import RemoveGroupModal from '@/views/Group/modals/RemoveGroupModal.vue'
import { computed, onMounted, ref } from 'vue'
import { matchesSearchAccentInsensitive } from '@/utils/string/search-normalize'
import { useRouter } from 'vue-router'

const isLoading = ref(true)
const isDeleting = ref(false)
const { t } = useI18n()
const isRemoveModalOpen = ref(false)
const isEditModalOpen = ref(false)
const selectedGroupName = ref('')
const selectedGroupId = ref(0)

const store = useGroupStore()
const groups = computed(() => store.groups)
const fetchGroups = store.fetch
const removeGroup = store.removeGroup

const searchQuery = ref('')

const filteredGroups = computed(() => {
  const q = searchQuery.value.trim()
  if (!q) return groups.value
  return groups.value.filter((g) => matchesSearchAccentInsensitive(g.name, q))
})

onMounted(() => {
  fetchGroups().then(() => {
    isLoading.value = false
  })
})

const openRemoveModal = (groupId: number, groupName: string) => {
  selectedGroupId.value = groupId
  selectedGroupName.value = groupName
  isRemoveModalOpen.value = true
}

const closeRemoveModal = () => {
  isRemoveModalOpen.value = false
  isDeleting.value = false
}

const openEditModal = (groupId: number, groupName: string) => {
  selectedGroupId.value = groupId
  selectedGroupName.value = groupName
  isEditModalOpen.value = true
}

const closeEditModal = () => {
  isEditModalOpen.value = false
}

const deleteGroup = async () => {
  isDeleting.value = true
  await removeGroup(selectedGroupId.value)
  isDeleting.value = false
  isRemoveModalOpen.value = false
}

const router = useRouter()

const navigateToCreateGroup = () => {
  router.push({ name: 'create-group', query: { name: searchQuery.value } })
}

function handleRefresh() {
  isLoading.value = true
  fetchGroups({ force: true }).then(() => {
    isLoading.value = false
  })
}

// Importando e utilizando a sensorStore para abrir os detalhes do sensor
const sensorStore = useSensorStore()
const openSensorDetails = (sensor) => {
	navigateToSensorDetail(sensor)
}

function navigateToSensorDetail(sensor) {
  router.push({ name: 'sensor-detail', params: { id: sensor.id } })
}
</script>

<template>
  <DefaultLayout>
    <Loader v-if="isLoading" />

    <div v-else class="mx-auto w-full max-w-[1400px] px-4 py-6">
      <PageHeader
        :to="{ name: 'dashboard' }"
        title="Grupos"
        subtitle="Organize seus sensores em grupos para facilitar a gestão.">
        <template #actions>
          <div class="flex items-center gap-2">
            <button
              type="button"
              class="flex items-center justify-center border border-stroke p-2 text-slate-700 transition hover:bg-slate-50"
              title="Atualizar"
              @click="handleRefresh">
              <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
            <ListingFilters>
              <template #default="{ close }">
                <input
                  v-model="searchQuery"
                  type="text"
                  class="w-full border border-stroke px-3 py-2 text-sm focus:border-primary focus:outline-none"
                  placeholder="Buscar por nome do grupo"
                  @keydown.enter="close" />
              </template>
            </ListingFilters>
            <button
              type="button"
              class="border border-primary bg-primary px-4 py-2 text-sm font-medium text-white transition hover:bg-primary/90"
              @click="navigateToCreateGroup">
              Criar grupo
            </button>
          </div>
        </template>
      </PageHeader>
      <div class="col-span-5 xl:col-span-3">
        <div v-for="group in filteredGroups" :key="group.id" class="my-6">
          <div class="relative overflow-x-auto border border-stroke bg-white px-4 py-6 shadow-sm">
              <div class="relative pr-10">
                <div class="flex items-center gap-2">
                  <button type="button" class="shrink-0 p-1 hover:opacity-80" @click="openEditModal(group.id, group.name)">
                    <IcEdit height="20" width="20" />
                  </button>
                  <h1 class="text-xl font-bold text-black-2">
                    {{ group.name }}
                  </h1>
                </div>
                <p v-if="group.observation" class="mt-1 pl-9">{{ group.observation }}</p>
                <div id="delete" class="absolute right-0 top-0">
                  <button type="button" class="hover:opacity-80" @click="openRemoveModal(group.id, group.name)">
                    <IcDeleteClose width="18" height="18" />
                  </button>
                </div>
              </div>
              <div class="mt-4 px-9">
                <span class="font-semibold">Sensores:</span>
                <ul class="list-disc">
                  <button
                    v-for="sensor in group.sensors"
                    :key="sensor.id"
                    :class="[
                      getSensorColor(sensor.sensor_type),
                      sensor.has_open_alarm && 'sensor-with-open-alarm'
                    ]"
                    class="m-4 mx-1 whitespace-nowrap px-3 py-2 text-sm font-medium text-white opacity-90 hover:opacity-100"
                    @click="openSensorDetails(sensor)">
                    {{ sensor.name }}
                  </button>
                </ul>
              </div>
              <div v-if="group.shifts?.length" class="mt-2 px-9">
                <span class="font-semibold text-slate-600">{{ t('groups.shifts') }}:</span>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span
                    v-for="shift in group.shifts"
                    :key="shift.id"
                    class="inline-flex items-center rounded-none bg-slate-700 px-3 py-2 text-sm font-medium text-white">
                    {{ shift.name }}
                  </span>
                </div>
              </div>
          </div>
        </div>
      </div>
    </div>

    <RemoveGroupModal :isOpen="isRemoveModalOpen" :isDeleting="isDeleting" :streamName="selectedGroupName" @close="closeRemoveModal" @confirm="deleteGroup" />
    <EditGroupModal :isOpen="isEditModalOpen" :streamName="selectedGroupName" :group-id="selectedGroupId" @close="closeEditModal" />
  </DefaultLayout>
</template>
